import _ from 'lodash';
import {
  endOfMonth,
  isBefore,
  isFirstDayOfMonth,
  isSameDay,
  subMonths,
} from 'date-fns';
import { format, startOfMonth } from 'date-fns/fp';
import flow from 'lodash/fp/flow';

import { ISO_DATE_FORMAT } from '@constants/dates';
import { createWindowKey } from '../utils';

import {
  getCashFlowProjection,
  GetCashFlowProjectionAsyncAction,
  getCategoryWindowRollups,
  GetCategoryWindowRollupsAsyncAction,
  getDailyAverageTransactionsCashFlow,
  GetDailyAverageTransactionsCashFlowAsyncAction,
  getDailyTransactionsCashFlow,
  GetDailyTransactionsCashFlowAsyncAction,
  getTopMerchants,
  GetTopMerchantsAsyncAction,
  getTransactionsMonthCashFlow,
  GetTransactionsMonthCashFlowAsyncAction,
} from '../actions';
import {
  getAllTransactionsFromBuckets,
  getOrCreateTransactionData,
  updateStateWithTransactionData,
  createMonthWindowKey,
  getSpendingByDayOfMonth,
  getTopSpendingSlice,
  updateTransactionsMonthCashFlow,
} from './helpers';
import { CASH_FLOW_CATEGORIES, TRANSACTION_CATEGORIES_V2 } from '../constants';
import type { AsyncAction } from '../../utils';

const createEmptyBuckets = () => ({
  all: undefined,
  category: {},
  categoryId: {},
  tag: {},
  merchantName: {},
  accountId: {},
});

const getInitialState = () => ({
  allTransactionsById: {},
  buckets: createEmptyBuckets(),
  categoryRollups: {},
  cashFlowRollups: {},
  topMerchants: {},
  categoryWindowRollups: {},
  dailyRollups: {},
  monthlyEstimates: {},
  resultsSetToken: undefined,
  transactionsOutdated: false,
  bulkLoadingInProgress: false,
  viewList: [],
  viewFilter: {},
});

const formatIsoString = format(ISO_DATE_FORMAT);

type Action<T extends Pick<AsyncAction, 'payload' | 'meta'>> = {
  type?: string;
  payload?: T['payload'];
  meta?: T['meta'] & {
    // Provided by Redux Easy Async
    actionName: string;
    asyncType: 'success' | 'fail';
    requestStartTime: string;
    asyncID: string;
  };
};

const transactionsReducer = (state, action: Action<any>) => {
  switch (action.type) {
    case getCashFlowProjection.SUCCESS_TYPE: {
      const {
        payload: { projectedEarning },
        meta: { dayInTargetMonth },
      } = action as Action<GetCashFlowProjectionAsyncAction>;

      const firstDayInTargetMonth = flow(
        startOfMonth,
        formatIsoString,
      )(dayInTargetMonth);

      const projected_bill_spending = null; // TODO

      return {
        ...state,
        monthlyEstimates: {
          ...state.monthlyEstimates,
          [firstDayInTargetMonth]: {
            ...state?.monthlyEstimates?.[firstDayInTargetMonth],
            [CASH_FLOW_CATEGORIES.EARNING]: projectedEarning,
            [CASH_FLOW_CATEGORIES.SPENDING]: projected_bill_spending,
          },
        },
      };
    }

    case getCategoryWindowRollups.SUCCESS_TYPE: {
      const {
        payload: { window },
        meta: { endDate, startDate, today },
      } = action as Action<GetCategoryWindowRollupsAsyncAction>;

      // The payload only contains categories with spending data,
      // so we manually add any missing categories here.
      const baseCategoryRollups = _.reduce(
        _.omit(TRANSACTION_CATEGORIES_V2, [
          TRANSACTION_CATEGORIES_V2.TRANSFERS,
          TRANSACTION_CATEGORIES_V2.INCOME,
        ]),
        (acc, category) => ({
          ...acc,
          [category]: 0,
        }),
        {},
      );

      const categoryRollups = _.reduce(
        window,
        (acc, { sum_amount: amount, category_v2: category, categoryId }) => ({
          ...acc,
          [categoryId ? parseInt(categoryId, 10) : category]: amount,
        }),
        baseCategoryRollups,
      );

      // The range represents the whole month if:
      // 1) the start of the range is the first of the month
      // 2) the end of the range is the end of the month
      //    OR
      //    in the current month, it is today or later
      const isForWholeMonth =
        isFirstDayOfMonth(startDate) &&
        (isSameDay(endDate, endOfMonth(endDate)) || isBefore(today, endDate));

      // If the range is for the whole month, update the "monthRollups" for the bucket
      let newState = state;
      if (isForWholeMonth) {
        const firstDayInTargetMonth = formatIsoString(startDate);
        newState = _.reduce(
          categoryRollups,
          (acc, categoryRollup, category) => {
            const bucket = getOrCreateTransactionData(state, { category });
            const newBucket = {
              ...bucket,
              monthRollups: {
                ...bucket.monthRollups,
                [firstDayInTargetMonth]: {
                  ...bucket.monthRollups[firstDayInTargetMonth],
                  sum: categoryRollup,
                },
              },
            };

            return updateStateWithTransactionData(acc, newBucket, { category });
          },
          state,
        );
      }

      return {
        ...newState,
        categoryWindowRollups: {
          ...state.categoryWindowRollups,
          [createWindowKey(startDate, endDate)]: categoryRollups,
        },
      };
    }

    case getDailyAverageTransactionsCashFlow.SUCCESS_TYPE: {
      const {
        payload: { averageSpendingPerDay },
        meta: { dayInEndMonth, dayInStartMonth },
      } = action as Action<GetDailyAverageTransactionsCashFlowAsyncAction>;

      const windowKey = createWindowKey(dayInStartMonth, dayInEndMonth);

      return {
        ...state,
        dailyRollups: {
          ...state.dailyRollups,
          [CASH_FLOW_CATEGORIES.SPENDING]: {
            ...state.dailyRollups.spending,
            [windowKey]: averageSpendingPerDay,
          },
        },
      };
    }

    case getDailyTransactionsCashFlow.SUCCESS_TYPE: {
      const {
        payload: { targetMonthSpending, previousMonthSpending },
        meta: { dayInTargetMonth },
      } = action as Action<GetDailyTransactionsCashFlowAsyncAction>;

      const targetMonthWindowKey = createMonthWindowKey(dayInTargetMonth);
      const previousMonthWindowKey = createMonthWindowKey(
        subMonths(dayInTargetMonth, 1),
      );
      const targetMonthDailySpending =
        getSpendingByDayOfMonth(targetMonthSpending);
      const previousMonthDailySpending = getSpendingByDayOfMonth(
        previousMonthSpending,
      );

      return {
        ...state,
        dailyRollups: {
          ...state.dailyRollups,
          [CASH_FLOW_CATEGORIES.SPENDING]: {
            ...state.dailyRollups.spending,
            [targetMonthWindowKey]: targetMonthDailySpending,
            [previousMonthWindowKey]: previousMonthDailySpending,
          },
        },
      };
    }

    case getTopMerchants.SUCCESS_TYPE: {
      const {
        payload,
        meta: { endDate, startDate },
      } = action as Action<GetTopMerchantsAsyncAction>;
      const { topMerchants } = state;
      const sliceNames = Object.keys(payload);

      const newTopMerchants = _.reduce(
        sliceNames,
        (acc, sliceName) => {
          const sliceData = payload?.[sliceName];
          const oldSlice = topMerchants?.[sliceName];
          const newSlice = getTopSpendingSlice(
            sliceData,
            sliceName,
            startDate,
            endDate,
          );

          return newSlice
            ? {
                ...acc,
                [sliceName]: {
                  ...oldSlice,
                  ...newSlice,
                },
              }
            : acc;
        },
        topMerchants,
      );

      return {
        ...state,
        topMerchants: newTopMerchants,
      };
    }

    case getTransactionsMonthCashFlow.SUCCESS_TYPE: {
      const {
        payload: { spending, earning },
        meta: { filter, dayInTargetMonth },
      } = action as Action<GetTransactionsMonthCashFlowAsyncAction>;

      return updateTransactionsMonthCashFlow(
        state,
        filter,
        dayInTargetMonth,
        spending,
        earning,
      );
    }

    default:
      return state;
  }
};

const reducer = (state = getInitialState(), action = {}) => {
  const newState = transactionsReducer(state, action);

  if (state.buckets !== newState.buckets) {
    // Anytime an action could have changed one of the transactions we want to update
    // the all transactions map
    const nextTransactions = getAllTransactionsFromBuckets(newState.buckets);
    return {
      ...newState,
      allTransactionsById: {
        ...newState.allTransactionsById,
        ...nextTransactions.reduce(
          (acc, transaction) => ({
            ...acc,
            [transaction.id]: transaction,
          }),
          {},
        ),
      },
    };
  }

  return newState;
};

export default reducer;
